<template>
  <div
    class="flex gap-4 fadeIn-0-3s"
    :class="{ relative: item.wasModifiedByStaff }"
  >
    <!-- Modified indicator badge -->
    <span
      v-if="item.wasModifiedByStaff"
      class="absolute -top-1 -left-1 w-3 h-3 rounded-full border-2 animate-pulse z-10 bg-red-500 border-white"
      :title="
        item.isNew
          ? 'New item added by staff'
          : `Quantity changed from ${item.originalQuantity} to ${item.newQuantity}`
      "
    />

    <!-- Item Image -->
    <div
      class="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border border-gray-100 mt-1 bg-gray-100"
    >
      <img
        v-if="itemImage"
        :src="itemImage"
        class="w-full h-full object-contain"
        :alt="item.name"
        @error="handleImageError"
      />
      <div
        v-else
        class="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200"
      >
        <FileImage :size="20" class="text-gray-400" />
      </div>
    </div>

    <!-- Item Content -->
    <div class="flex-1">
      <div class="flex justify-between items-start mb-1">
        <h4 class="font-semibold text-gray-900 line-clamp-1 text-base">
          {{ item.name }}
        </h4>
        <div class="text-right">
          <div v-if="item.hasDiscount" class="mb-0.5">
            <span class="text-xs text-gray-400 line-through">
              {{ formatPrice(item.itemTotalWithoutDiscount) }}
            </span>
          </div>
          <span class="font-medium text-primary-600">
            {{ formatPrice(item.itemTotal) }}
          </span>
        </div>
      </div>

      <!-- Modifiers -->
      <p
        v-if="item.modifiersText"
        class="text-xs text-gray-500 mb-1 leading-relaxed"
      >
        {{ item.modifiersText }}
      </p>

      <!-- Note -->
      <p v-if="item.note" class="text-xs text-gray-500 mb-2 italic">
        "{{ item.note }}"
      </p>

      <!-- Alert message -->
      <Alert
        v-if="item.alertMessage"
        variant="warning"
        :message="item.alertMessage"
        :icon="AlertCircle"
        :dismissible="false"
        custom-class="mt-1.5 mb-2"
      />

      <!-- Quantity controls -->
      <div class="flex items-center justify-between mt-3">
        <div
          class="flex items-center bg-gray-100 rounded-lg shadow-sm"
          :class="{ 'opacity-50': isDisabled }"
        >
          <Button
            @click="handleDecrease"
            :disabled="isDisabled || item.quantity <= 1"
            variant="ghost"
            :custom-class="
              `${useQuantityDropdown ? 'w-12 h-12 md:w-10 md:h-10' : 'w-9 h-9'}
              flex items-center justify-center text-gray-400 hover:bg-gray-200 hover:text-primary-600 disabled:opacity-30 transition-colors rounded-l-lg
              ${isDisabled ? 'cursor-not-allowed' : ''}`
            "
            size="sm"
          >
            <Minus :size="16" style="stroke-width: 3;" />
          </Button>
          <select
            v-if="useQuantityDropdown"
            :value="item.quantity"
            :disabled="isDisabled"
            @change="handleQuantitySelect"
            aria-label="Select quantity"
            class="h-12 min-w-[5rem] border-x border-gray-200 bg-white px-3 text-center text-lg font-bold outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed md:h-10 md:min-w-[4.5rem] md:text-base"
            :class="isDisabled ? 'text-gray-600' : 'text-gray-900'"
          >
            <option
              v-for="quantityOption in quantityOptions"
              :key="quantityOption"
              :value="quantityOption"
            >
              {{ quantityOption }}
            </option>
          </select>
          <span
            v-if="!useQuantityDropdown"
            class="w-10 text-center text-base font-bold"
            :class="isDisabled ? 'text-gray-600' : 'text-gray-900'"
          >
            {{ item.quantity }}
          </span>
          <Button
            @click="handleIncrease"
            :disabled="isDisabled"
            variant="ghost"
            :custom-class="
              `${useQuantityDropdown ? 'w-12 h-12 md:w-10 md:h-10' : 'w-9 h-9'}
              flex items-center justify-center text-gray-400 hover:bg-gray-200 hover:text-primary-600 disabled:opacity-30 transition-colors rounded-r-lg
              ${isDisabled ? 'cursor-not-allowed' : ''}`
            "
            size="sm"
          >
            <Plus :size="16" style="stroke-width: 3;" />
          </Button>
        </div>
        <div v-if="!isDisabled" class="flex items-center gap-1">
          <Button
            @click="handleEdit"
            variant="ghost"
            custom-class="flex items-center gap-1.5 px-2.5 py-1.5 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors text-sm font-medium"
            size="sm"
            title="Edit item"
          >
            <Pencil :size="16" class="text-primary-600" />
            <span>Edit</span>
          </Button>
          <Button
            @click="handleRemove"
            variant="ghost"
            custom-class="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
            circular
            size="sm"
          >
            <Trash2 :size="20" />
          </Button>
        </div>
        <div
          v-else
          class="p-2 text-gray-300 cursor-not-allowed rounded-full"
          title="This item cannot be removed or modified from an existing order"
        >
          <Trash2 :size="20" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Button, Alert } from "@/components/ui";
import {
  getQuantitySelectOptions,
  isPoloBistroUrl
} from "@/utils/customStoreRules";
import {
  FileImage,
  AlertCircle,
  Plus,
  Minus,
  Trash2,
  Pencil
} from "lucide-vue";

export default {
  name: "TableCartItem",
  components: {
    Button,
    Alert
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    currency: {
      type: String,
      default: "$"
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update-quantity", "remove-item", "edit-item"],
  data() {
    return {
      FileImage,
      AlertCircle,
      Plus,
      Minus,
      Trash2,
      Pencil,
      imageError: false
    };
  },
  computed: {
    ...mapGetters(["formatPrice"]),
    itemImage() {
      if (this.imageError) return null;
      return this.item?.image || this.item?.image_url || null;
    },
    useQuantityDropdown() {
      return isPoloBistroUrl(this.$route?.path);
    },
    quantityOptions() {
      return getQuantitySelectOptions(this.item?.quantity);
    }
  },
  methods: {
    handleImageError(event) {
      this.imageError = true;
      if (event.target) {
        event.target.style.display = "none";
      }
    },
    handleDecrease() {
      if (this.isDisabled) return;
      this.$emit("update-quantity", this.item.cartId, -1);
    },
    handleIncrease() {
      if (this.isDisabled) return;
      this.$emit("update-quantity", this.item.cartId, 1);
    },
    handleQuantitySelect(event) {
      if (this.isDisabled) return;

      const selectedQuantity = Number(event.target.value);
      const currentQuantity = Number(this.item.quantity) || 1;
      const delta = selectedQuantity - currentQuantity;

      if (delta !== 0) {
        this.$emit("update-quantity", this.item.cartId, delta);
      }
    },
    handleRemove() {
      if (this.isDisabled) return;
      this.$emit("remove-item", this.item.cartId);
    },
    handleEdit() {
      if (this.isDisabled) return;
      this.$emit("edit-item", this.item);
    }
  }
};
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.fadeIn-0-3s {
  animation: fadeIn 0.3s ease-out;
}
</style>
